/**
 * Micro‑utilidades de DOM. Todo el texto se inserta con nodos de texto
 * (nunca innerHTML con datos), lo que previene XSS por construcción.
 */
export type Child = Node | string | number | null | undefined | false | Child[];

type Handler = (event: never) => void;
export interface Props {
  class?: string;
  style?: Partial<Record<string, string>> | string;
  dataset?: Record<string, string>;
  attrs?: Record<string, string | number | boolean | null | undefined>;
  [key: string]: unknown;
}

const BOOLEAN_PROPS = new Set(['disabled', 'checked', 'hidden', 'selected', 'readOnly', 'required', 'autofocus', 'multiple', 'open']);

function appendChildren(parent: Node, children: Child[]): void {
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    if (Array.isArray(child)) appendChildren(parent, child);
    else if (child instanceof Node) parent.appendChild(child);
    else parent.appendChild(document.createTextNode(String(child)));
  }
}

function applyProps(el: Element, props: Props | null | undefined): void {
  if (!props) return;
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null) continue;
    if (key === 'class') el.setAttribute('class', String(value));
    else if (key === 'style') {
      if (typeof value === 'string') el.setAttribute('style', value);
      else for (const [prop, v] of Object.entries(value as Record<string, string>)) (el as HTMLElement).style.setProperty(prop, v);
    } else if (key === 'dataset') Object.assign((el as HTMLElement).dataset, value);
    else if (key === 'attrs') {
      for (const [attr, v] of Object.entries(value as Record<string, unknown>)) {
        if (v === false || v === null || v === undefined) continue;
        el.setAttribute(attr, v === true ? '' : String(v));
      }
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2).toLowerCase(), value as EventListener);
    } else if (BOOLEAN_PROPS.has(key)) {
      (el as unknown as Record<string, boolean>)[key] = Boolean(value);
    } else if (key in el && typeof value !== 'object') {
      (el as unknown as Record<string, unknown>)[key] = value;
    } else {
      el.setAttribute(key, String(value));
    }
  }
}

/** Crea un elemento HTML: h('button', { class: 'btn', onClick }, 'Texto'). */
export function h<K extends keyof HTMLElementTagNameMap>(tag: K, props?: Props | null, ...children: Child[]): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  applyProps(el, props);
  appendChildren(el, children);
  return el;
}

const SVG_NS = 'http://www.w3.org/2000/svg';

/** Crea un elemento SVG. */
export function s<K extends keyof SVGElementTagNameMap>(tag: K, attrs?: Record<string, string | number | undefined> | null, ...children: Child[]): SVGElementTagNameMap[K] {
  const el = document.createElementNS(SVG_NS, tag);
  if (attrs) for (const [k, v] of Object.entries(attrs)) if (v !== undefined) el.setAttribute(k, String(v));
  appendChildren(el, children);
  return el;
}

export function clear(el: Element): void {
  while (el.firstChild) el.removeChild(el.firstChild);
}

export function replace(el: Element, ...children: Child[]): void {
  clear(el);
  appendChildren(el, children);
}

export function fragment(...children: Child[]): DocumentFragment {
  const frag = document.createDocumentFragment();
  appendChildren(frag, children);
  return frag;
}

/** Registra un listener y devuelve la función para eliminarlo. */
export function listen<K extends keyof WindowEventMap>(target: Window, type: K, handler: (e: WindowEventMap[K]) => void, options?: AddEventListenerOptions): () => void;
export function listen<K extends keyof DocumentEventMap>(target: Document, type: K, handler: (e: DocumentEventMap[K]) => void, options?: AddEventListenerOptions): () => void;
export function listen<K extends keyof HTMLElementEventMap>(target: HTMLElement, type: K, handler: (e: HTMLElementEventMap[K]) => void, options?: AddEventListenerOptions): () => void;
export function listen(target: EventTarget, type: string, handler: Handler, options?: AddEventListenerOptions): () => void {
  target.addEventListener(type, handler as EventListener, options);
  return () => target.removeEventListener(type, handler as EventListener, options);
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
